import React, { forwardRef } from 'react';
import { cn } from '../../utils/cn';
import Icon from '../AppIcon';
import { useClickAnimation, useRippleEffect, useHoverAnimation } from '../../hooks/useAnimations';

/**
 * AnimatedButton - Enhanced button with micro-interactions
 * 
 * Features:
 * - Ripple effect on click
 * - Hover animations
 * - Click feedback
 * - Loading states
 * - Icon support
 * - Accessibility optimized
 */
const AnimatedButton = forwardRef(({
  children,
  variant = 'default',
  size = 'medium',
  icon,
  iconPosition = 'left',
  iconSize = 16,
  loading = false,
  disabled = false,
  ripple = true,
  hoverAnimation = true,
  clickAnimation = true,
  className,
  onClick,
  ...props
}, ref) => {
  const { elementRef: clickRef, handleClick: handleClickAnimation } = useClickAnimation('btn-press');
  const { elementRef: rippleRef, handleClick: handleRipple } = useRippleEffect();
  const { elementRef: hoverRef, handleMouseEnter, handleMouseLeave } = useHoverAnimation('hover-scale');

  // Combine refs
  const combinedRef = (node) => {
    if (ref) ref.current = node;
    clickRef.current = node;
    rippleRef.current = node;
    hoverRef.current = node;
  };

  const handleClick = (e) => {
    if (disabled || loading) return;

    if (clickAnimation) {
      handleClickAnimation(e);
    }

    if (ripple) {
      handleRipple(e);
    }

    if (onClick) {
      onClick(e);
    }
  };

  const variantClasses = {
    default: 'bg-primary text-primary-foreground hover:bg-primary/90',
    secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
    outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
    ghost: 'hover:bg-accent hover:text-accent-foreground',
    link: 'text-primary underline-offset-4 hover:underline',
    destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
    success: 'bg-success text-success-foreground hover:bg-success/90',
    warning: 'bg-warning text-warning-foreground hover:bg-warning/90'
  };

  const sizeClasses = {
    sm: 'h-9 px-3 text-sm',
    medium: 'h-10 px-4 py-2',
    lg: 'h-11 px-8 text-lg',
    xl: 'h-12 px-10 text-xl',
    icon: 'h-10 w-10'
  };

  const baseClasses = cn(
    'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
    variantClasses[variant],
    sizeClasses[size],
    hoverAnimation && 'hover-lift',
    className
  );

  const iconElement = icon && (
    <Icon 
      name={icon} 
      size={iconSize} 
      className={cn(
        'flex-shrink-0',
        iconPosition === 'left' && 'mr-2',
        iconPosition === 'right' && 'ml-2'
      )}
    />
  );

  const loadingSpinner = loading && (
    <div className="spinner mr-2" />
  );

  return (
    <button
      ref={combinedRef}
      className={baseClasses}
      disabled={disabled || loading}
      onClick={handleClick}
      onMouseEnter={hoverAnimation ? handleMouseEnter : undefined}
      onMouseLeave={hoverAnimation ? handleMouseLeave : undefined}
      {...props}
    >
      {loading && loadingSpinner}
      {!loading && icon && iconPosition === 'left' && iconElement}
      {children}
      {!loading && icon && iconPosition === 'right' && iconElement}
    </button>
  );
});

AnimatedButton.displayName = 'AnimatedButton';

export default AnimatedButton;
